// frontend/src/components/chat/ChatMessage.jsx
// PHASE 5 UPDATE: Added reactions and enhanced read receipts
// PHASE 6: Added system message support for group events
// Individual message bubble with edit, delete, reactions, and attachment support

import React, { useState, useRef, useEffect } from "react";
import { useChat } from "../../contexts/ChatContext";
import { chatApi } from "../../utils/api";
import MessageAttachment from "./MessageAttachment";

// Common emoji reactions for quick selection
const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🎉"];

const ChatMessage = ({ message, isGroup = false }) => {
  const { currentUserId, editMessage, deleteMessage, getMessageHistory } =
    useChat();

  // ALL useState hooks must be at the top, called unconditionally
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.content || "");
  const [isSaving, setIsSaving] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editHistory, setEditHistory] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isReacting, setIsReacting] = useState(false);
  const [localReactions, setLocalReactions] = useState(message.reactions || []);

  // ALL useRef hooks
  const editInputRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Derived values (not hooks)
  const isMine = message.sender_id === currentUserId || message.is_mine;
  const hasAttachment = message.attachment || message.has_attachment;
  const isDeleted = message.is_deleted;
  const isSystemMessage =
    message.message_type === "system" || message.message_type === "System";

  // ALL useEffect hooks must be called unconditionally
  useEffect(() => {
    if (isEditing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.setSelectionRange(
        editInputRef.current.value.length,
        editInputRef.current.value.length
      );
    }
  }, [isEditing]);

  // Sync local reactions with message prop when it updates
  useEffect(() => {
    setLocalReactions(message.reactions || []);
  }, [message.reactions]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Get read receipt status
  const getReadReceiptStatus = () => {
    if (!isMine) return null;
    if (message.read_at || message.read_status === true || message.is_read) {
      return "read";
    }
    if (message.delivered_at) {
      return "delivered";
    }
    return "sent";
  };

  // Handle edit start
  const handleStartEdit = () => {
    setEditText(message.content || "");
    setIsEditing(true);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditText(message.content || "");
    setIsEditing(false);
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    const trimmedText = editText.trim();
    if (!trimmedText || trimmedText === message.content) {
      handleCancelEdit();
      return;
    }

    setIsSaving(true);
    try {
      await editMessage(message.message_id, trimmedText);
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to edit message:", err);
      alert("Failed to edit message. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle key press in edit input
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  // Handle view edit history
  const handleViewHistory = async () => {
    setShowHistoryModal(true);
    setIsLoadingHistory(true);

    try {
      const data = await getMessageHistory(message.message_id);
      setEditHistory(data.history || []);
    } catch (err) {
      console.error("Failed to fetch edit history:", err);
      setEditHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Handle delete click
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteMessage(message.message_id);
      setShowDeleteConfirm(false);
    } catch (err) {
      console.error("Failed to delete message:", err);
      alert("Failed to delete message. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle emoji reaction
  const handleReaction = async (emoji) => {
    if (isReacting) return;

    setIsReacting(true);
    setShowEmojiPicker(false);

    try {
      const existingReaction = localReactions.find((r) => r.emoji === emoji);
      const userHasReacted = existingReaction?.users?.some(
        (u) => u.user_id === currentUserId
      );

      if (userHasReacted) {
        await chatApi.removeReaction(message.message_id, emoji);
        setLocalReactions((prev) =>
          prev
            .map((r) => {
              if (r.emoji === emoji) {
                const newUsers = r.users.filter(
                  (u) => u.user_id !== currentUserId
                );
                return newUsers.length > 0
                  ? { ...r, users: newUsers, count: newUsers.length }
                  : null;
              }
              return r;
            })
            .filter(Boolean)
        );
      } else {
        await chatApi.addReaction(message.message_id, emoji);
        setLocalReactions((prev) => {
          const existing = prev.find((r) => r.emoji === emoji);
          if (existing) {
            return prev.map((r) =>
              r.emoji === emoji
                ? {
                    ...r,
                    users: [...r.users, { user_id: currentUserId }],
                    count: r.count + 1,
                  }
                : r
            );
          } else {
            return [
              ...prev,
              {
                emoji,
                count: 1,
                users: [{ user_id: currentUserId }],
              },
            ];
          }
        });
      }
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
      setLocalReactions(message.reactions || []);
    } finally {
      setIsReacting(false);
    }
  };

  // Check if current user has reacted with a specific emoji
  const hasUserReacted = (emoji) => {
    const reaction = localReactions.find((r) => r.emoji === emoji);
    return reaction?.users?.some((u) => u.user_id === currentUserId);
  };

  // Render read receipt icon
  const renderReadReceipt = () => {
    const status = getReadReceiptStatus();
    if (!status) return null;

    switch (status) {
      case "read":
        return (
          <span className="read-receipt read-receipt-read" title="Read">
            <i className="bi bi-check2-all"></i>
          </span>
        );
      case "delivered":
        return (
          <span
            className="read-receipt read-receipt-delivered"
            title="Delivered"
          >
            <i className="bi bi-check2-all"></i>
          </span>
        );
      case "sent":
      default:
        return (
          <span className="read-receipt read-receipt-sent" title="Sent">
            <i className="bi bi-check2"></i>
          </span>
        );
    }
  };

  // Render reactions display
  const renderReactions = () => {
    if (!localReactions || localReactions.length === 0) return null;

    return (
      <div className="message-reactions">
        {localReactions.map((reaction) => (
          <button
            key={reaction.emoji}
            className={`reaction-badge ${
              hasUserReacted(reaction.emoji) ? "user-reacted" : ""
            }`}
            onClick={() => handleReaction(reaction.emoji)}
            title={reaction.users
              ?.map((u) => u.user_name || u.first_name || "User")
              .join(", ")}
            disabled={isReacting}
          >
            <span className="reaction-emoji">{reaction.emoji}</span>
            <span className="reaction-count">{reaction.count}</span>
          </button>
        ))}
      </div>
    );
  };

  // ==================== SYSTEM MESSAGE RENDERING ====================
  // System messages are used for group events like user joined, left, etc.
  if (isSystemMessage) {
    return (
      <div
        id={`message-${message.message_id}`}
        className="message-row message-system"
      >
        <div className="system-message-content">
          <i className="bi bi-info-circle me-1"></i>
          <span className="system-message-text">
            {message.sender_name && <strong>{message.sender_name} </strong>}
            {message.content}
          </span>
          <span className="system-message-time">
            {formatTime(message.created_at)}
          </span>
        </div>
      </div>
    );
  }

  // ==================== DELETED MESSAGE RENDERING ====================
  if (isDeleted) {
    return (
      <div
        id={`message-${message.message_id}`}
        className={`message-row ${isMine ? "message-mine" : "message-theirs"}`}
      >
        <div className="message-content">
          <div className="message-bubble message-deleted">
            <div className="message-deleted-text">
              <i className="bi bi-trash me-1"></i>
              This message was deleted
            </div>
            <div className="message-meta">
              <span className="message-time">
                {formatTime(message.deleted_at || message.created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== REGULAR MESSAGE RENDERING ====================
  return (
    <>
      <div
        id={`message-${message.message_id}`}
        className={`message-row ${isMine ? "message-mine" : "message-theirs"}`}
      >
        <div className="message-content">
          {/* Sender name for received messages (always show in groups) */}
          {!isMine && (isGroup || message.sender_name) && (
            <div className="message-sender">{message.sender_name}</div>
          )}

          {/* Message bubble */}
          <div className={`message-bubble ${isEditing ? "editing" : ""}`}>
            {/* Edit mode */}
            {isEditing ? (
              <div className="message-edit-inline">
                <textarea
                  ref={editInputRef}
                  className="message-edit-input"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isSaving}
                  rows={Math.min(5, Math.max(1, editText.split("\n").length))}
                  placeholder="Type your message..."
                />
                <div className="message-edit-actions">
                  <button
                    className="btn btn-sm btn-link text-muted"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleSaveEdit}
                    disabled={isSaving || !editText.trim()}
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
                <div className="message-edit-hint">
                  Press Enter to save, Escape to cancel
                </div>
              </div>
            ) : (
              <>
                {/* Message text */}
                {message.content && (
                  <div className="message-text">{message.content}</div>
                )}

                {/* Attachment */}
                {hasAttachment && message.attachment && (
                  <MessageAttachment
                    attachment={message.attachment}
                    isMine={isMine}
                  />
                )}

                {/* Message meta */}
                <div className="message-meta">
                  <span className="message-time">
                    {formatTime(message.created_at)}
                  </span>
                  {message.is_edited && (
                    <span
                      className="message-edited"
                      onClick={handleViewHistory}
                      title="Click to view edit history"
                    >
                      (edited)
                    </span>
                  )}
                  {renderReadReceipt()}
                </div>
              </>
            )}
          </div>

          {/* Reactions display */}
          {renderReactions()}

          {/* Action buttons - show for all messages when not editing */}
          {!isEditing && (
            <div className="message-actions">
              {/* Reaction button */}
              <div className="reaction-picker-container" ref={emojiPickerRef}>
                <button
                  className="message-action-btn"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  title="Add reaction"
                  disabled={isReacting}
                >
                  <i className="bi bi-emoji-smile"></i>
                </button>

                {/* Emoji picker dropdown */}
                {showEmojiPicker && (
                  <div className="emoji-picker-dropdown">
                    {QUICK_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        className={`emoji-option ${
                          hasUserReacted(emoji) ? "selected" : ""
                        }`}
                        onClick={() => handleReaction(emoji)}
                        disabled={isReacting}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Edit and delete - only for own messages */}
              {isMine && (
                <>
                  <button
                    className="message-action-btn"
                    onClick={handleStartEdit}
                    title="Edit message"
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    className="message-action-btn message-action-delete"
                    onClick={handleDeleteClick}
                    title="Delete message"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </>
              )}
            </div>
          )}

          {/* Delete confirmation popup */}
          {showDeleteConfirm && (
            <div className="delete-confirm-popup">
              <div className="delete-confirm-text">Delete this message?</div>
              <div className="delete-confirm-actions">
                <button
                  className="btn btn-sm btn-link text-muted"
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit History Modal */}
      {showHistoryModal && (
        <div
          className="modal-backdrop show"
          onClick={() => setShowHistoryModal(false)}
        >
          <div
            className="chat-history-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="chat-history-header">
              <h6>Edit History</h6>
              <button
                className="btn btn-link text-muted p-0"
                onClick={() => setShowHistoryModal(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="chat-history-content">
              {isLoadingHistory ? (
                <div className="text-center py-3">
                  <span className="spinner-border spinner-border-sm"></span>
                  <span className="ms-2">Loading history...</span>
                </div>
              ) : editHistory && editHistory.length > 0 ? (
                <>
                  {/* Current version */}
                  <div className="history-item current">
                    <div className="history-label">
                      Current
                      <span className="history-time">
                        {formatTime(message.updated_at || message.created_at)}
                      </span>
                    </div>
                    <div className="history-text">{message.content}</div>
                  </div>

                  {/* Previous versions */}
                  {editHistory.map((item, index) => (
                    <div key={index} className="history-item">
                      <div className="history-label">
                        Previous
                        <span className="history-time">
                          {formatTime(item.edited_at)}
                        </span>
                      </div>
                      <div className="history-text">{item.content}</div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-center text-muted py-3">
                  No edit history available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatMessage;
